/**
 * Linhas de corte (crop marks) espelhando o SEM-TEXTO da Lambda.
 *
 * Cada etiqueta tem 8 segmentos (4 cantos × H/V) que saem ~27 pt da caixa.
 * Em slots vizinhos os ticks se encontram no vão e formam uma reta contínua.
 * O y_offset de −0,21 pt da coluna direita (legado) é ignorado só nos ticks,
 * para as retas horizontais não saírem tortas.
 *
 * Coordenadas em PDF (origem canto inferior esquerdo), iguais ao ReportLab.
 */
import { rgb, LineCapStyle } from "pdf-lib";
import { ARTWORK_LAYOUT } from "./labelAssets";
import { LABEL_SLOTS } from "./layout/constants";

const TICK_LEN = 27;
const INSET = 5.5;
const WHITE_WIDTH = 5;
const BLACK_WIDTH = 1;
/** Meia largura do traço branco: quanto a cap redonda avança além do ponto. */
const CUT_MARK_OUTSET = WHITE_WIDTH / 2;

/** y do slot esquerdo da mesma linha — deixa ticks H colineares. */
const cutMarkSlot = (slot, indexOnPage) => {
  const rowLeft = LABEL_SLOTS[Math.floor(indexOnPage / 2) * 2] || slot;
  return {
    x_offset: slot.x_offset || 0,
    y_offset: rowLeft.y_offset || 0,
  };
};

const slotBox = (slot) => {
  const left = ARTWORK_LAYOUT.x + (slot.x_offset || 0);
  const bottom = ARTWORK_LAYOUT.y + (slot.y_offset || 0);
  return {
    left,
    bottom,
    right: left + ARTWORK_LAYOUT.width,
    top: bottom + ARTWORK_LAYOUT.height,
  };
};

/**
 * Segmentos H/V externos de uma caixa `{ left, bottom, right, top }`, origem PDF.
 *
 * `outset` afasta o início de cada segmento da aresta (para fora). Como o traço
 * usa cap redonda, a cap avança `WHITE_WIDTH / 2` além do ponto inicial: com
 * `outset = CUT_MARK_OUTSET` ela encosta na aresta sem entrar na área visível.
 * Papéis legados chamam com `outset` 0 (saída idêntica à histórica).
 */
const segmentsForBox = ({ left, bottom, right, top }, tickLen, outset = 0) => {
  const yHi = top - INSET;
  const yLo = bottom + INSET;
  const xLeft = left + INSET;
  const xRight = right - INSET;
  const outLeft = left - outset;
  const outRight = right + outset;
  const outTop = top + outset;
  const outBottom = bottom - outset;

  return [
    { x1: outLeft, y1: yHi, x2: outLeft - tickLen, y2: yHi },
    { x1: outRight, y1: yHi, x2: outRight + tickLen, y2: yHi },
    { x1: outLeft, y1: yLo, x2: outLeft - tickLen, y2: yLo },
    { x1: outRight, y1: yLo, x2: outRight + tickLen, y2: yLo },
    { x1: xLeft, y1: outTop, x2: xLeft, y2: outTop + tickLen },
    { x1: xRight, y1: outTop, x2: xRight, y2: outTop + tickLen },
    { x1: xLeft, y1: outBottom, x2: xLeft, y2: outBottom - tickLen },
    { x1: xRight, y1: outBottom, x2: xRight, y2: outBottom - tickLen },
  ];
};

/** Segmentos H/V externos de um slot, já com origem PDF. */
const cutMarkSegmentsForSlot = (slot) => segmentsForBox(slotBox(slot), TICK_LEN);

/** Branco grosso por baixo + preto fino por cima (paridade com o SEM-TEXTO). */
const strokeSegments = (page, segments) => {
  const stroke = (color, thickness) => {
    for (const s of segments) {
      page.drawLine({
        start: { x: s.x1, y: s.y1 },
        end: { x: s.x2, y: s.y2 },
        thickness,
        color,
        lineCap: LineCapStyle.Round,
      });
    }
  };

  stroke(rgb(1, 1, 1), WHITE_WIDTH);
  stroke(rgb(0, 0, 0), BLACK_WIDTH);
};

/**
 * Desenha ticks de corte para os slots ocupados (índice = posição na página).
 * Branco grosso por baixo + preto fino por cima (paridade com o SEM-TEXTO).
 */
const drawCutMarks = (page, slots) => {
  const segments = [];
  slots.forEach((slot, index) => {
    segments.push(...cutMarkSegmentsForSlot(cutMarkSlot(slot, index)));
  });
  strokeSegments(page, segments);
};

/**
 * Desenha ticks de corte para caixas quaisquer (papel `art`: uma caixa
 * combinada frente+verso por carta), sem depender da grade legada de 10 slots
 * do LABEL_SLOTS. `outset` empurra os segmentos para fora da caixa.
 */
const drawCutMarksForBoxes = (page, boxes, { tickLen = 12, outset = 0 } = {}) => {
  const segments = boxes.flatMap((box) => segmentsForBox(box, tickLen, outset));
  strokeSegments(page, segments);
};

export {
  TICK_LEN,
  INSET,
  WHITE_WIDTH,
  CUT_MARK_OUTSET,
  cutMarkSlot,
  cutMarkSegmentsForSlot,
  drawCutMarks,
  drawCutMarksForBoxes,
  slotBox,
};
